// "What stands out" for the Overview: plain-language findings across the selected games,
// ranked by importance so the biggest things are visible without clicking through.
import React from 'react';
import moment from 'moment';
import Icon from '../../../components/atoms/Icon';
import { QUEUE_TIME_GOOD_MAX } from '../concessionTheme';
import { formatDuration } from '../live/liveMetrics';
import '../concession.css';

const day = (g) => moment(g.date).format('MMM D');
const avgOf = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
const median = (arr) => {
  if (!arr.length) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};
const relativeToStart = (minutes) => {
  const m = Math.round(Math.abs(minutes));
  if (m < 5) return 'right at game start';
  const text = m >= 60 ? formatDuration(m) : `${m} min`;
  return minutes < 0 ? `${text} before game start` : `${text} after game start`;
};

// Build ranked findings. games: Overview game cards (with per-section metrics); sections: section ids.
export const buildInsights = ({ games, sections, isCountMode, formatTransactionTime }) => {
  const withData = games.filter((g) => g.hasData);
  if (!withData.length) return [];
  const unit = isCountMode ? 'people' : 'min';
  const multi = withData.length >= 2;
  const insights = [];

  // 1. Overflow (queue mode only)
  if (!isCountMode) {
    const overflowGames = withData.filter((g) => g.overflow.minutes > 0);
    if (overflowGames.length) {
      const total = overflowGames.reduce((sum, g) => sum + g.overflow.minutes, 0);
      const bySection = sections
        .map((id) => ({ id, minutes: withData.reduce((sum, g) => sum + (g.sections?.[id]?.overflow || 0), 0) }))
        .sort((a, b) => b.minutes - a.minutes);
      const top = bySection[0]?.minutes > 0 ? bySection[0] : null;
      insights.push({
        key: 'overflow',
        tone: 'bad',
        icon: 'exclamation-triangle-fill',
        score: 100,
        label: 'Overflow',
        text: multi ? (
          <>
            Overflow in <strong>{overflowGames.length} of {withData.length}</strong> games
            {' '}(<strong>{formatDuration(total)}</strong> total)
            {top && sections.length > 1 && <>, mostly <strong>Section {top.id}</strong> ({formatDuration(top.minutes)})</>}.
          </>
        ) : (
          <>
            Overflow for <strong>{formatDuration(total)}</strong>
            {top && sections.length > 1 && <>, mostly <strong>Section {top.id}</strong> ({formatDuration(top.minutes)})</>}.
          </>
        ),
        chips: overflowGames,
      });
    }
  }

  // 2. Problem section: busiest on average, and how often it was the busiest
  if (sections.length > 1) {
    const sectionAvgs = sections
      .map((id) => {
        const vals = withData.map((g) => g.sections?.[id]).filter((m) => m?.hasData).map((m) => m.avg);
        return { id, avg: avgOf(vals), n: vals.length };
      })
      .filter((s) => s.n > 0)
      .sort((a, b) => b.avg - a.avg);
    const top = sectionAvgs[0];
    if (top) {
      const timesBusiest = withData.filter((g) => g.busiest?.id === top.id).length;
      const overTarget = !isCountMode && Math.round(top.avg) >= QUEUE_TIME_GOOD_MAX;
      insights.push({
        key: 'section',
        tone: overTarget ? 'bad' : 'info',
        icon: 'geo-alt-fill',
        score: overTarget ? 80 : 50,
        label: 'Busiest section',
        text: multi ? (
          <>
            <strong>Section {top.id}</strong> was the busiest section in <strong>{timesBusiest} of {withData.length}</strong> games,
            averaging <strong>{Math.round(top.avg)} {unit}</strong>.
          </>
        ) : (
          <>
            <strong>Section {top.id}</strong> was the busiest section, averaging <strong>{Math.round(top.avg)} {unit}</strong>.
          </>
        ),
      });
    }
  }

  // 3. Worst game vs the rest
  if (multi) {
    const sorted = [...withData].sort((a, b) => b.avg - a.avg);
    const worst = sorted[0];
    const others = avgOf(sorted.slice(1).map((g) => g.avg));
    const diff = Math.round(worst.avg - others);
    if (diff >= 1) {
      insights.push({
        key: 'worst',
        tone: !isCountMode && Math.round(worst.avg) >= QUEUE_TIME_GOOD_MAX ? 'bad' : 'info',
        icon: 'arrow-up-right-circle-fill',
        score: 70 + Math.min(diff, 10),
        label: 'Busiest game',
        text: (
          <>
            <strong>{day(worst)}</strong> had the {isCountMode ? 'most people in line' : 'longest queues'}:
            {' '}<strong>{worst.avg} {unit}</strong> average, <strong>{diff} {unit}</strong> more than the other games.
          </>
        ),
        chips: [worst],
      });
    }
  }

  // 4. Smoothest game (least time over target)
  if (multi) {
    const best = [...withData].sort((a, b) => a.over.share - b.over.share)[0];
    const onTarget = Math.round((1 - best.over.share) * 100);
    insights.push({
      key: 'best',
      tone: 'good',
      icon: 'check-circle-fill',
      score: 40,
      label: 'Smoothest game',
      text: (
        <>
          <strong>{day(best)}</strong> ran smoothest: {isCountMode ? 'not busy' : 'on target'} <strong>{onTarget}%</strong> of the time.
        </>
      ),
      chips: [best],
    });
  } else {
    const g = withData[0];
    insights.push({
      key: 'over',
      tone: g.over.minutes > 0 ? 'bad' : 'good',
      icon: g.over.minutes > 0 ? 'clock-history' : 'check-circle-fill',
      score: 60,
      label: isCountMode ? 'Busy time' : 'Time over target',
      text: g.over.minutes > 0
        ? <>{isCountMode ? 'Busy' : 'Over target'} for <strong>{formatDuration(g.over.minutes)}</strong> ({Math.round(g.over.share * 100)}% of the window).</>
        : <>{isCountMode ? 'Never busy' : 'On target the whole time'} in this window.</>,
    });
  }

  // 5. When queues peak, relative to game start
  const offsets = withData.map((g) => g.peakOffset).filter((v) => typeof v === 'number');
  if (offsets.length) {
    const med = median(offsets);
    const spread = Math.max(...offsets) - Math.min(...offsets);
    insights.push({
      key: 'timing',
      tone: 'info',
      icon: 'clock-fill',
      score: 45,
      label: 'When it peaks',
      text: multi ? (
        <>
          {isCountMode ? 'Counts' : 'Queues'} peaked about <strong>{relativeToStart(med)}</strong>
          {spread <= 45 ? ' in every game' : ' in most games'}.
        </>
      ) : (
        <>
          {isCountMode ? 'Counts' : 'Queues'} peaked <strong>{relativeToStart(med)}</strong>.
        </>
      ),
    });
  }

  // 6. Slowest transactions vs the rest
  if (!isCountMode && multi) {
    const sorted = [...withData].filter((g) => g.txnAvg > 0).sort((a, b) => b.txnAvg - a.txnAvg);
    if (sorted.length >= 2) {
      const slow = sorted[0];
      const others = avgOf(sorted.slice(1).map((g) => g.txnAvg));
      const diff = Math.round(slow.txnAvg - others);
      if (diff >= 10) {
        insights.push({
          key: 'txn',
          tone: 'info',
          icon: 'credit-card-fill',
          score: 35,
          label: 'Transactions',
          text: (
            <>
              Transactions were slowest on <strong>{day(slow)}</strong>: <strong>{formatTransactionTime(slow.txnAvg)}</strong> average,
              {' '}{formatTransactionTime(diff)} slower than the other games.
            </>
          ),
          chips: [slow],
        });
      }
    }
  }

  return insights.sort((a, b) => b.score - a.score).slice(0, 6);
};

// Defined outside OverviewInsights so React keeps each card between updates (a card defined
// inside the component was rebuilt on every render, replaying its fade-in, which flickered on live refreshes)
const InsightCard = ({ insight, lead: isLead }) => (
  <div className={`cx-in-card cx-in-${insight.tone}${isLead ? ' cx-in-lead' : ''} cx-fade-in`}>
    <span className="cx-in-icon"><Icon name={insight.icon} /></span>
    <div className="cx-in-body">
      <div className="cx-in-label">{insight.label}</div>
      <div className="cx-in-text">{insight.text}</div>
      {insight.chips && insight.chips.length > 0 && (
        <div className="cx-in-chips">
          {insight.chips.map((g) => (
            <span key={g.date} className="cx-in-chip">
              <span className="cx-ov-swatch" style={{ background: g.color }} />
              {day(g)}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

const OverviewInsights = ({ insights }) => {
  if (!insights.length) return null;
  const [lead, ...rest] = insights;
  return (
    <div className="cx-in">
      <InsightCard key={lead.key} insight={lead} lead />
      {rest.length > 0 && (
        <div className="cx-in-grid">
          {rest.map((insight) => (
            <InsightCard key={insight.key} insight={insight} />
          ))}
        </div>
      )}
    </div>
  );
};

export default OverviewInsights;
